import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import {
  QrCode,
  Mail,
  Video,
  CreditCard,
  Scan,
  Link as LinkIcon,
  User,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const ToolsPage = () => {
  const navigate = useNavigate();

  const tools = [
    {
      title: 'Email Signature Generator',
      description: 'Create professional email signatures for Gmail, Outlook, and more',
      icon: Mail,
      path: '/email-signature-generator',
      color: 'blue',
      bgGradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Meeting Background Generator',
      description: 'Create branded backgrounds for Zoom, Teams, and Google Meet',
      icon: Video,
      path: '/meeting-themes-generator',
      color: 'purple',
      bgGradient: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Digital Business Card Maker',
      description: 'Design beautiful digital visiting cards with multiple themes',
      icon: User,
      path: '/card-builder',
      color: 'green',
      bgGradient: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <Layout>
      <SEO
        title="Free Tools | QR Code, Email Signature, Business Card Tools"
        description="Free online tools for professionals. QR code generator, email signature maker, meeting backgrounds, digital business cards, vCard creator, and more."
        keywords="free qr code generator, email signature generator, meeting background maker, digital business card tool, vcard generator, link in bio"
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              All Free Tools
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Free Professional Tools
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to create a professional digital presence. All tools are free to use.
            </p>
          </div>

          {/* Tools Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => (
              <Card 
                key={index}
                className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-0 overflow-hidden"
                onClick={() => navigate(tool.path)}
              >
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-r ${tool.bgGradient} p-6 text-white`}>
                    <tool.icon className="w-10 h-10 mb-3" />
                    <h3 className="text-xl font-bold">{tool.title}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">{tool.description}</p>
                    <div className="flex items-center text-amber-600 font-medium group-hover:gap-2 transition-all">
                      <span>Use Tool</span>
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-3">Need a Complete Digital Presence?</h2>
              <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                Create your own NFC-enabled digital business card with all your links, contact info, and social profiles in one place.
              </p>
              <button
                onClick={() => navigate('/card-builder')}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-3 rounded-full transition-colors"
              >
                Create Your Digital Card
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ToolsPage;
